import { useEffect, useRef } from 'react';
import { useConstructor } from './store/useConstructor';
import { useIframeScene } from './useIframeScene';
import { LAB_SCENE_PARAM } from './labScenes';
import type { ComponentTemplate, Scene } from './schema/components';
import type { ConstructorValue } from './types';

export interface ConstructorPreviewProps {
  token: string;
  gameId: string;
  template: ComponentTemplate;
  /** Origin URL of the iframe app to embed (e.g. https://play.repackz.fun
   *  in production, http://localhost:7474 in dev, https://repackz-game-staging.pages.dev
   *  on staging). Required — каждый host app передаёт свой. */
  iframeOrigin: string;
  /** Fired when the iframe navigates to a different sub-route. The parent
   *  uses this to filter the sidebar component list down to the scene the
   *  operator is currently looking at. `null` while we don't know yet
   *  (initial mount, cross-origin) — caller should fall back to "show
   *  everything". */
  onSceneChange?: (scene: Scene | null) => void;
  /**
   * The custom pack lab state to open the preview on (`labScenes.ts`), sent as `&lab_scene=`; null or absent for none,
   * which leaves the page as it is today. A change reloads the preview, which gets the theme again on load.
   */
  labScene?: string | null | undefined;
}

/**
 * Embedded iframe that loads the iframe-app in "child" mode. The parent
 * Constructor posts ConstructorValue updates via postMessage; the child
 * applies them to its own :root.
 *
 * Two posting paths handle iframe lifecycle:
 *   - useEffect on state.value — posts when user edits in editor.
 *   - onLoad handler — posts current state on every iframe load (initial
 *     load and any reload triggered by src changes, e.g. template switch
 *     or token/gameId changes).
 */
export function ConstructorPreview({
  token,
  gameId,
  template,
  iframeOrigin,
  onSceneChange,
  labScene = null,
}: ConstructorPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { state } = useConstructor();
  const stateRef = useRef<ConstructorValue>(state.value);
  stateRef.current = state.value;

  // Watch the iframe's pathname so the parent can filter its sidebar to
  // components actually present on the visible scene.
  const scene = useIframeScene(iframeRef, template);
  useEffect(() => {
    onSceneChange?.(scene);
  }, [scene, onSceneChange]);

  const labSceneParam = labScene ? `&${LAB_SCENE_PARAM}=${encodeURIComponent(labScene)}` : '';
  const src = `${iframeOrigin}/?session_token=${encodeURIComponent(token)}&game_id=${encodeURIComponent(gameId)}&template=${template}&design_constructor=child${labSceneParam}`;

  // Post latest state on every state change. May be a no-op if iframe
  // not yet ready (listener not subscribed) — onLoad covers that path.
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'design-constructor-apply', value: state.value },
      '*',
    );
  }, [state.value]);

  // Post current state every time iframe reloads (initial + on src change).
  const handleLoad = () => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'design-constructor-apply', value: stateRef.current },
      '*',
    );
  };

  return (
    <iframe
      ref={iframeRef}
      title="Design Constructor preview"
      src={src}
      onLoad={handleLoad}
      className="h-full w-full border-0"
    />
  );
}
